<template>
  <div class="cr-page us-page">
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />

    <CreateUserDialog
      v-model="showCreateDialog"
      :outgoing-enabled="outgoingEnabled"
      :existing-users="users"
      @saved="onSaved"
      @error="showError"
    />

    <EditUserDialog
      v-model="showEditDialog"
      :user="userToEdit"
      :existing-users="users"
      @saved="onSaved"
      @error="showError"
    />

    <ToggleUserStatusDialog
      v-model="showToggleStatusDialog"
      :user="userToToggle"
      @saved="onSaved"
      @error="showError"
    />

    <div class="cr-panel">
      <div class="cr-panel__toolbar us-panel__toolbar">
        <div class="cr-panel__toolbar-start">
          <UsersAgentTypeTabs
            v-model="roleTab"
            v-model:agent-type="agentTypeTab"
            :admin-count="adminMembersCount"
            :agent-count="agentMembersCount"
            :ai-count="aiMembersCount"
            :human-count="humanMembersCount"
          />
          <span class="cr-panel__count">
            {{ tabFilteredUsers.length }} member{{
              tabFilteredUsers.length === 1 ? "" : "s"
            }}
          </span>
          <v-select
            v-model="statusFilter"
            :items="statusFilterOptions"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            class="us-status-filter"
            @update:model-value="loadUsers"
          />
        </div>
        <UsersHeader
          :loading="loading"
          :is-session-ready="isSessionReady"
          :is-search-active="isSearchActive"
          @add="showCreateDialog = true"
          @refresh="loadUsers"
        />
      </div>

      <UsersList
        :users="paginatedUsers"
        :loading="loading"
        :is-search-active="isSearchActive"
        :role-tab="roleTab"
        :agent-type-tab="agentTypeTab"
        :current-user-id="currentUserId"
        @add="showCreateDialog = true"
        @edit="openEdit"
        @delete="confirmDelete"
        @reset-password="confirmResetPassword"
        @toggle-status="confirmToggleStatus"
        @set-extension="openSetExtension"
      />

      <PortalListPagination
        class="cr-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="tabFilteredUsers.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>

    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card rounded="xl" class="user-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="dialog-header__icon">
              <v-icon color="error" size="22">mdi-account-remove</v-icon>
            </div>
            <span class="dialog-header__title">Remove team member</span>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="dialog-close-btn"
            rounded="lg"
            :disabled="deletingId != null"
            @click="deleteDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="dialog-body dialog-body--message">
          <strong class="dialog-body__name">{{ userToDelete?.name }}</strong>
          ({{ userToDelete?.email }}) will lose portal access. This cannot be
          undone.
        </v-card-text>
        <v-card-actions class="dialog-footer">
          <v-spacer />
          <v-btn
            variant="text"
            class="text-none dialog-btn dialog-btn--muted"
            rounded="lg"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            class="text-none dialog-btn dialog-btn--danger"
            rounded="lg"
            :loading="deletingId != null"
            @click="handleDelete"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="extensionDialog" max-width="460" persistent>
      <v-card rounded="xl" class="user-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="dialog-header__icon dialog-header__icon--primary">
              <v-icon color="primary" size="22">mdi-phone-settings-outline</v-icon>
            </div>
            <span class="dialog-header__title">Softphone extension</span>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="dialog-close-btn"
            rounded="lg"
            :disabled="savingExtension"
            @click="extensionDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="dialog-body">
          <p class="mb-0">
            Provision a SIP softphone extension for
            <strong class="dialog-body__name">{{ userForExtension?.name }}</strong>.
            Credentials are generated server-side — the agent picks them up
            automatically the next time they open the portal
            (<code>getMySoftphoneRegistration</code>).
          </p>
        </v-card-text>
        <v-card-actions class="dialog-footer">
          <v-spacer />
          <v-btn
            variant="text"
            class="text-none dialog-btn dialog-btn--muted"
            rounded="lg"
            :disabled="savingExtension"
            @click="extensionDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="text-none btn-primary-cta"
            rounded="lg"
            :loading="savingExtension"
            @click="handleSetExtension"
          >
            Provision extension
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resetDialog" max-width="460" persistent>
      <v-card rounded="xl" class="user-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="dialog-header__icon">
              <v-icon color="primary" size="22">mdi-lock-reset</v-icon>
            </div>
            <span class="dialog-header__title">Reset password</span>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="dialog-close-btn"
            rounded="lg"
            :disabled="resettingId != null"
            @click="resetDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="dialog-body dialog-body--message">
          Send a password reset for
          <strong class="dialog-body__name">{{ userToReset?.name }}</strong>
          ({{ userToReset?.email }})? They will need to set a new password on
          next sign-in.
        </v-card-text>
        <v-card-actions class="dialog-footer">
          <v-spacer />
          <v-btn
            variant="text"
            class="text-none dialog-btn dialog-btn--muted"
            rounded="lg"
            @click="resetDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="text-none btn-primary-cta"
            rounded="lg"
            :loading="resettingId != null"
            @click="handleResetPassword"
          >
            Reset password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import orgUsersApi from "@/mixins/Users/orgUsersApi";
import CreateUserDialog from "@/components/Users/CreateUserDialog.vue";
import EditUserDialog from "@/components/Users/EditUserDialog.vue";
import ToggleUserStatusDialog from "@/components/Users/ToggleUserStatusDialog.vue";
import UsersHeader from "@/components/Users/UsersHeader.vue";
import UsersAgentTypeTabs from "@/components/Users/UsersAgentTypeTabs.vue";
import UsersList from "@/components/Users/UsersList.vue";
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import PortalListPagination from "@/components/Extras/PortalListPagination.vue";
import { getApiMessage, isApiSuccess } from "@/mixins/Extras/ApiResponse";
import { unwrapApiList } from "@/utils/graphql";
import { normalizeUserRecord } from "@/utils/session";
import { setAgentSoftphoneExtension } from "@/api/softphoneApi";
import { USER_STATUS_OPTIONS } from "@/constants/userStatus";
import {
  AGENT_TYPE,
  USER_ROLE,
  isAiAgent,
  isHumanAgent,
  resolveListAgentType,
} from "@/utils/orgUsers";

const SEARCH_FIELDS = [
  "name",
  "email",
  "role",
  "status",
  "agentType",
  "recoveryEmail",
  "recoveryWhatsapp",
  "id",
];

export default {
  name: "UsersPage",
  components: {
    CreateUserDialog,
    EditUserDialog,
    ToggleUserStatusDialog,
    UsersHeader,
    UsersAgentTypeTabs,
    UsersList,
    SnackbarComp,
    PortalListPagination,
  },
  mixins: [orgUsersApi],
  data() {
    return {
      SnackbarComponent: {},
      users: [],
      loading: false,
      deletingId: null,
      resettingId: null,
      showCreateDialog: false,
      showEditDialog: false,
      showToggleStatusDialog: false,
      deleteDialog: false,
      resetDialog: false,
      userToDelete: null,
      userToEdit: null,
      userToToggle: null,
      userToReset: null,
      extensionDialog: false,
      userForExtension: null,
      savingExtension: false,
      statusFilter: "",
      roleTab: USER_ROLE.ADMIN,
      agentTypeTab: AGENT_TYPE.AI,
      page: 1,
      itemsPerPage: 10,
      statusFilterOptions: [
        { title: "All statuses", value: "" },
        ...USER_STATUS_OPTIONS,
      ],
    };
  },
  computed: {
    ...mapState(["tableSearch", "currentUserId", "organization"]),
    ...mapGetters(["isSessionReady"]),
    /**
     * Agents (AI or human) only exist to be assigned to outbound
     * campaigns/tasks -- when an org has outgoing disabled (BMS flag),
     * there's nothing for them to do, so they're hidden from the Team
     * list entirely (this is also what filters out the auto-provisioned
     * "<org name> AI Agent" row every org gets at onboarding).
     */
    outgoingEnabled() {
      return this.organization?.outgoing_enabled !== false;
    },
    scopedUsers() {
      if (this.outgoingEnabled) return this.users;
      return this.users.filter(
        (user) => this.resolveUserRole(user) !== USER_ROLE.AGENT,
      );
    },
    searchFilteredUsers() {
      const q = (this.tableSearch || "").trim().toLowerCase();
      if (!q) return this.scopedUsers;
      return this.scopedUsers.filter((user) =>
        SEARCH_FIELDS.some((field) =>
          String(user[field] ?? "")
            .toLowerCase()
            .includes(q),
        ),
      );
    },
    adminMembers() {
      // OWNER is administrative too; only AGENT-role users go to the agent tab.
      return this.searchFilteredUsers.filter(
        (user) => this.resolveUserRole(user) !== USER_ROLE.AGENT,
      );
    },
    agentMembers() {
      return this.searchFilteredUsers.filter(
        (user) => this.resolveUserRole(user) === USER_ROLE.AGENT,
      );
    },
    adminMembersCount() {
      return this.adminMembers.length;
    },
    agentMembersCount() {
      return this.agentMembers.length;
    },
    aiMembersCount() {
      return this.agentMembers.filter((user) =>
        isAiAgent(resolveListAgentType(user)),
      ).length;
    },
    humanMembersCount() {
      return this.agentMembers.filter((user) =>
        isHumanAgent(resolveListAgentType(user)),
      ).length;
    },
    tabFilteredUsers() {
      if (this.roleTab === USER_ROLE.ADMIN) {
        return this.adminMembers;
      }
      return this.agentMembers.filter((user) => {
        const agentType = resolveListAgentType(user);
        return this.agentTypeTab === AGENT_TYPE.AI
          ? isAiAgent(agentType)
          : isHumanAgent(agentType);
      });
    },
    pageCount() {
      if (this.itemsPerPage <= 0) return 1;
      return Math.max(
        1,
        Math.ceil(this.tabFilteredUsers.length / this.itemsPerPage),
      );
    },
    paginatedUsers() {
      if (this.itemsPerPage <= 0) return this.tabFilteredUsers;
      const start = (this.page - 1) * this.itemsPerPage;
      return this.tabFilteredUsers.slice(start, start + this.itemsPerPage);
    },
    rangeLabel() {
      const total = this.tabFilteredUsers.length;
      if (!total) return "0 of 0";
      if (this.itemsPerPage <= 0) return `1-${total} of ${total}`;
      const start = (this.page - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.page * this.itemsPerPage, total);
      return `${start}-${end} of ${total}`;
    },
    isSearchActive() {
      return Boolean((this.tableSearch || "").trim());
    },
  },
  watch: {
    isSessionReady: {
      immediate: true,
      handler(ready) {
        if (ready) this.loadUsers();
      },
    },
    tabFilteredUsers() {
      if (this.page > this.pageCount) {
        this.page = this.pageCount;
      }
    },
    roleTab() {
      this.page = 1;
    },
    agentTypeTab() {
      this.page = 1;
    },
    tableSearch() {
      this.page = 1;
    },
  },
  methods: {
    resolveUserRole(user) {
      // Only OWNER/ADMIN roles belong to the Admin tab; everyone else is
      // grouped under Agent and split by their agent type.
      const role = String(user?.role ?? "").trim().toUpperCase();
      return role === "OWNER" || role === USER_ROLE.ADMIN
        ? USER_ROLE.ADMIN
        : USER_ROLE.AGENT;
    },
    setPage(next) {
      this.page = Math.min(Math.max(1, next), this.pageCount);
    },
    setItemsPerPage(next) {
      this.itemsPerPage = next;
      this.page = 1;
    },
    normalizeUsers(raw) {
      const list = unwrapApiList(raw);
      return list.map(normalizeUserRecord).filter(Boolean);
    },
    onSaved(response) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: "success",
        SnackbarText: getApiMessage(response, "Saved successfully"),
      };
      this.loadUsers();
    },
    showError(payload) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: "error",
        SnackbarText: getApiMessage(payload, "Something went wrong"),
      };
    },
    buildListInput() {
      const input = {};
      if (this.statusFilter) {
        input.user_status = this.statusFilter;
      }
      return input;
    },
    async loadUsers() {
      if (!this.isSessionReady) return;

      this.loading = true;
      try {
        const response = await this.listOrgUsers(this.buildListInput());
        if (!isApiSuccess(response)) {
          this.showError(response);
          this.users = [];
          return;
        }
        this.users = this.normalizeUsers(response);
        this.page = 1;
      } finally {
        this.loading = false;
      }
    },
    openEdit(item) {
      this.userToEdit = item;
      this.showEditDialog = true;
    },
    confirmDelete(item) {
      this.userToDelete = item;
      this.deleteDialog = true;
    },
    confirmResetPassword(item) {
      this.userToReset = item;
      this.resetDialog = true;
    },
    confirmToggleStatus(item) {
      this.userToToggle = item;
      this.showToggleStatusDialog = true;
    },
    openSetExtension(item) {
      this.userForExtension = item;
      this.extensionDialog = true;
    },
    async handleSetExtension() {
      const userId = String(this.userForExtension?.id ?? "").trim();
      if (!userId) return;

      this.savingExtension = true;
      try {
        // Server auto-provisions extension + password; only user_id is sent.
        const response = await setAgentSoftphoneExtension({ user_id: userId });
        if (!isApiSuccess(response)) {
          this.showError(response);
          return;
        }
        const extension = response?.data?.extension;
        this.extensionDialog = false;
        this.userForExtension = null;
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "success",
          SnackbarText: getApiMessage(
            response,
            extension
              ? `Softphone extension ${extension} provisioned.`
              : "Softphone extension provisioned.",
          ),
        };
      } finally {
        this.savingExtension = false;
      }
    },
    async handleDelete() {
      const item = this.userToDelete;
      if (!item?.id && !item?.email) return;

      this.deletingId = item.id || item.email;
      try {
        const payload = {}
        if (item.id) payload.user_id = item.id
        const email = String(item.email ?? '').trim()
        if (email && email !== '—') payload.user_email_id = email

        const response = await this.deleteOrgUser(payload);
        if (isApiSuccess(response)) {
          this.deleteDialog = false;
          this.userToDelete = null;
          this.onSaved(response);
        } else {
          this.showError(response);
        }
      } finally {
        this.deletingId = null;
      }
    },
    async handleResetPassword() {
      const item = this.userToReset;
      if (!item?.id) return;

      this.resettingId = item.id;
      try {
        const response = await this.resetOrgUserPassword({ user_id: item.id });
        if (isApiSuccess(response)) {
          this.resetDialog = false;
          this.userToReset = null;
          this.onSaved(response);
        } else {
          this.showError(response);
        }
      } finally {
        this.resettingId = null;
      }
    },
  },
};
</script>

<style scoped>
.dialog-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--y-radius-md);
  background: rgba(248, 113, 113, 0.15);
}
</style>
